import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { BarChart3, Download, RefreshCw, ArrowLeft, AlertCircle, Info } from 'lucide-react'
import { toast } from 'sonner'
import { Bar, BarChart, XAxis, CartesianGrid, LabelList } from 'recharts'
import { whatsappApi } from '../api/whatsapp'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/shared/components/ui/chart'
import type { AnalyticsDetallado } from '../types'

const chartConfig = {
  value: {
    label: 'Mensajes',
    color: '#14b8a6',
  },
} satisfies ChartConfig

export function AnalyticsWhatsApp() {
  const { envioId } = useParams<{ envioId: string }>()
  const [analytics, setAnalytics] = useState<AnalyticsDetallado | null>(null)
  const [loading, setLoading] = useState(true)
  const [sincronizando, setSincronizando] = useState(false)
  const [exportando, setExportando] = useState(false)
  const [showInfo, setShowInfo] = useState(false)

  useEffect(() => {
    if (envioId) {
      cargarAnalytics()
    }
  }, [envioId])

  const cargarAnalytics = async () => {
    if (!envioId) return

    setLoading(true)
    try {
      const data = await whatsappApi.getAnalyticsDetallado(parseInt(envioId))
      setAnalytics(data)
    } catch (error) {
      console.error('Error cargando analytics:', error)
      toast.error('Error al cargar los analytics')
    } finally {
      setLoading(false)
    }
  }

  const sincronizarConTwilio = async () => {
    if (!envioId) return

    setSincronizando(true)
    try {
      const response = await whatsappApi.sincronizarEstado(parseInt(envioId))
      toast.success(response.mensaje || 'Sincronización completada')
      await cargarAnalytics()
    } catch (error) {
      console.error('Error en sincronización:', error)
      toast.error('Error al sincronizar con Twilio')
    } finally {
      setSincronizando(false)
    }
  }

  const exportarExcel = async () => {
    if (!envioId || !analytics) return

    setExportando(true)
    try {
      const blob = await whatsappApi.exportarExcel(
        parseInt(envioId),
        analytics.numeros_unicos
      )

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `analytics_${envioId}_${Date.now()}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('Excel exportado exitosamente')
    } catch (error) {
      console.error('Error al exportar:', error)
      toast.error('Error al exportar a Excel')
    } finally {
      setExportando(false)
    }
  }

  // Preparar datos para el gráfico de barras horizontales
  const getChartData = () => {
    if (!analytics) return []
    const stats = analytics.estadisticas
    const exitosos = stats.total - stats.errores
    return [
      { name: 'Exitosos', value: exitosos, fill: '#14b8a6' },
      { name: 'Enviados', value: stats.enviados, fill: '#0d9488' },
      { name: 'Entregados', value: stats.entregados, fill: '#0d9488' },
      { name: 'Leídos', value: stats.leidos, fill: '#0d9488' },
      { name: 'Clics', value: stats.clics, fill: '#0d9488' },
      { name: 'Errores', value: stats.errores, fill: '#ef4444' },
    ]
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
          <p className="text-sm text-muted-foreground">Cargando analytics...</p>
        </div>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="space-y-4">
        <Link to="/whatsapp/historial">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al historial
          </Button>
        </Link>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No se encontraron datos de analytics para este envío
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const stats = analytics.estadisticas

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Link to="/whatsapp/historial">
            <Button variant="ghost" size="sm" className="mb-2">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al historial
            </Button>
          </Link>
          <div className="flex items-center">
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-teal-500" />
              Analytics Detallado
            </h1>
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="text-muted-foreground hover:text-foreground transition-colors ml-2"
            >
              <Info className="h-5 w-5" />
            </button>
            {showInfo && (
              <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                {analytics.asunto}
              </span>
            )}
          </div>
          {analytics.plantilla_nombre && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-muted-foreground">Plantilla:</span>
              <Badge variant="secondary" className="capitalize">{analytics.plantilla_nombre}</Badge>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={sincronizarConTwilio}
            disabled={sincronizando}
          >
            <RefreshCw className={`h-4 w-4 ${sincronizando ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            className="bg-teal-800 hover:bg-teal-900 text-white"
            onClick={exportarExcel}
            disabled={exportando}
          >
            <Download className="h-4 w-4 mr-2" />
            {exportando ? 'Exportando...' : 'Exportar Excel'}
          </Button>
        </div>
      </div>

      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Total Mensajes</p>
              <p className="text-3xl font-bold">{stats.total}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Mensajes Exitosos</p>
              <div className="flex items-center gap-2">
                <p className="text-3xl font-bold">{stats.total - stats.errores}</p>
                <Badge className="badge-gradient hover:opacity-90">
                  {stats.total > 0 ? (((stats.total - stats.errores) / stats.total) * 100).toFixed(1) : 0}%
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Tasa de Entrega</p>
              <p className="text-3xl font-bold">
                {stats.tasa_entrega.toFixed(1)}%
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Tasa de Lectura</p>
              <p className="text-3xl font-bold">
                {stats.tasa_lectura.toFixed(1)}%
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Tasa de Clics</p>
              <p className="text-3xl font-bold">
                {stats.tasa_clics.toFixed(1)}%
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Gráfico de distribución */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Distribución de Estados</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[220px] w-full">
              <BarChart accessibilityLayer data={getChartData()} margin={{ top: 20 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  fontSize={9}
                  interval={0}
                />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent hideLabel />}
                />
                <Bar dataKey="value" fill="#14b8a6" radius={6}>
                  <LabelList dataKey="value" position="top" fontSize={11} />
                </Bar>
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Detalles de errores */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              Errores Detallados
              {analytics.errores_detallados.length > 0 && (
                <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                  {analytics.errores_detallados.length}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[220px] overflow-y-auto">
              {analytics.errores_detallados.length > 0 ? (
                analytics.errores_detallados.map((error, i) => (
                  <div
                    key={i}
                    className="border-b pb-2 last:border-0 last:pb-0"
                  >
                    <p className="font-medium text-sm">{error.numero}</p>
                    <p className="text-sm text-red-600 mt-1">{error.error}</p>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center h-[180px] text-muted-foreground">
                  No hay errores registrados
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Estadísticas adicionales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center gap-2">
              <p className="text-2xl font-bold">{stats.enviados}</p>
              <p className="text-sm text-muted-foreground">Enviados</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center gap-2">
              <p className="text-2xl font-bold">{stats.entregados}</p>
              <p className="text-sm text-muted-foreground">Entregados</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center gap-2">
              <p className="text-2xl font-bold">{stats.leidos}</p>
              <p className="text-sm text-muted-foreground">Leídos</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center gap-2">
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.errores}</p>
              <p className="text-sm text-red-600 dark:text-red-400">Errores</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
